'use client';

import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import MaterialLink from '@/components/material-link';
import MaterialLinkButton from '@/components/material-link-button';
import { momentParse } from '@/utils/time';
import type { EventSeries } from '@/types/EventSeries';
import { eventPath, eventSeriesPath } from '@/utils/slugify';
import type { Event } from '@/types/Event';

type Props = {
  eventSeries: EventSeries;
  lastEvent?: Event;
};

const EventSeriesNoUpcomingEvents = ({ eventSeries, lastEvent }: Props) => {
  const hostLink = eventSeries.hostType === 'Group' && eventSeries.group
    ? `/groups/${eventSeries.group.slug}`
    : eventSeries.user
      ? `/riders/${eventSeries.user.username}`
      : null;

  const hostName = eventSeries.hostType === 'Group' && eventSeries.group
    ? eventSeries.group.name
    : eventSeries.user?.displayName;

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h3" sx={{ mt: 0, mb: 1 }}>
        No upcoming events scheduled
      </Typography>
      {lastEvent && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Last event:{' '}
          <MaterialLink href={eventPath(lastEvent.id, lastEvent.name)}>
            {lastEvent.name}
          </MaterialLink>
          {' '}on {momentParse(lastEvent.startAt, lastEvent.timezone).format('MMMM D, YYYY')}
        </Typography>
      )}
      {hostLink && hostName && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Want to know if this series is still happening?
          </Typography>
          <MaterialLinkButton
            href={hostLink}
            variant="outlined"
            size="small"
          >
            Visit {hostName}
          </MaterialLinkButton>
        </>
      )}
      {eventSeries.permissions?.editable && (
        <MaterialLinkButton
          href={`${eventSeriesPath(eventSeries.id, eventSeries.name)}/schedule`}
          variant="contained"
          color="primary"
          size="small"
          sx={{ ml: hostLink ? 1 : 0, mt: hostLink ? 0 : 2 }}
        >
          Schedule events
        </MaterialLinkButton>
      )}
    </Paper>
  );
};

export default EventSeriesNoUpcomingEvents;
